/**
 * WebConsentCard — inline consent control rendered when a web_search tool call
 * returns artifact_type="web_consent_request".
 *
 * The customer must EXPLICITLY enable web search for this conversation before
 * Ion will run it: once external/unverified results enter the chat, the
 * verified-Batemo-data guarantee no longer holds. Clicking "Enable" POSTs to
 * the per-conversation grant endpoint (sets a server-side flag the model can't
 * set itself), then nudges the AI to proceed. Until then, web_search returns
 * no results.
 */
import { useState } from 'preact/hooks'
import { authenticatedFetch } from '../../services/authService.js'
import { getApiBase } from '../admin/analytics/shared/formatters.js'
import { currentConversationId } from '../../state/chatStore.js'
import { sendChatMessage } from '../../hooks/useChatStream.js'

export function WebConsentCard({ payload }) {
  const [state, setState] = useState('idle') // idle|granting|granted|declined|error

  const enable = async () => {
    const convId = currentConversationId.value
    if (!convId) {
      setState('error')
      return
    }
    setState('granting')
    try {
      const r = await authenticatedFetch(
        `${getApiBase()}/chat/conversations/${encodeURIComponent(convId)}/web-access`,
        { method: 'POST' }
      )
      if (!r.ok && r.status !== 204) {
        setState('error')
        return
      }
      setState('granted')
      // Consent recorded server-side — ask the AI to proceed now.
      sendChatMessage('Yes, please search the web now.', {})
    } catch {
      setState('error')
    }
  }

  return (
    <div class="ion-webconsent" role="group" aria-label="Web search consent">
      <p class="ion-webconsent__title">
        <span aria-hidden="true">🌐</span>{' '}
        {payload?.title || 'Web search needs your approval'}
      </p>
      {/* preserve the backend message's line breaks so the egress/privacy
          bullets (incl. "your query is sent to a US provider") render as a
          list, not a run-on — this is the user's informed-consent surface. */}
      <p class="ion-webconsent__msg" style="white-space: pre-line">
        {payload?.message}
      </p>
      {state === 'idle' && (
        <div class="ion-webconsent__actions">
          <button type="button" class="ion-webconsent__enable" onClick={enable}>
            Enable web search
          </button>
          <button
            type="button"
            class="ion-webconsent__decline"
            onClick={() => setState('declined')}
          >
            Not now
          </button>
        </div>
      )}
      {state === 'granting' && (
        <p class="ion-webconsent__status" aria-live="polite">Enabling…</p>
      )}
      {state === 'granted' && (
        <p class="ion-webconsent__status" aria-live="polite">
          ✓ Web search enabled for this chat
        </p>
      )}
      {state === 'declined' && (
        <p class="ion-webconsent__status">Web search stays off.</p>
      )}
      {state === 'error' && (
        <p class="ion-webconsent__status ion-webconsent__status--error" aria-live="polite">
          Couldn’t enable web search — please try again.
        </p>
      )}
    </div>
  )
}
